import { X, Lock, ArrowLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "./ui/button"
import { useTranslation } from "../i18n"

export default function ModuleLockModal({ 
  isOpen, 
  onClose, 
  lockedModule, 
  previousModule,
  previousLesson 
}) {
  const { tObj } = useTranslation()
  const navigate = useNavigate()

  if (!isOpen || !lockedModule) return null

  const handleGoToPreviousModule = () => {
    if (previousModule) {
      navigate(`/courses/${lockedModule.courseId}/modules/${previousModule.id}`)
      onClose()
    }
  }

  const handleGoToPreviousLesson = () => {
    if (previousLesson && previousLesson.moduleId) {
      navigate(`/courses/${lockedModule.courseId}/modules/${previousLesson.moduleId}/lessons/${previousLesson.id}`)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-5 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {tObj.modals.moduleLock.title()}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
          >
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-4 sm:mb-5 md:mb-6">
          <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4 break-words">
            {previousLesson
              ? tObj.modals.moduleLock.mustCompletePreviousLesson?.({ previousTitle: previousLesson.title, lockedTitle: lockedModule.title }) || 
                `Vous devez compléter le chapitre précédente "${previousLesson.title}" avant d'accéder à "${lockedModule.title}"`
              : previousModule 
              ? tObj.modals.moduleLock.mustValidatePrevious({ previousTitle: previousModule.title, lockedTitle: lockedModule.title })
              : tObj.modals.moduleLock.moduleLocked({ lockedTitle: lockedModule.title })}
          </p>
          
          {previousLesson && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm text-blue-800 font-medium mb-1 sm:mb-2">
                {tObj.modals.moduleLock.previousLessonToComplete?.() || "Chapitre précédent à compléter"}
              </p>
              <p className="text-xs sm:text-sm text-blue-700 break-words">
                {previousLesson.title}
              </p>
            </div>
          )}
          
          {previousModule && !previousLesson && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm text-blue-800 font-medium mb-1 sm:mb-2">
                {tObj.modals.moduleLock.previousModuleToComplete()}
              </p>
              <p className="text-xs sm:text-sm text-blue-700 break-words">
                {previousModule.title}
              </p>
              {previousModule.quiz?.quiz && (
                <div className="mt-2">
                  <p className="text-xs text-blue-600">
                    {previousModule.quiz.isPassed 
                      ? tObj.modals.moduleLock.quizValidated()
                      : previousModule.quiz.bestAttempt 
                        ? tObj.modals.moduleLock.bestScore({ score: previousModule.quiz.bestAttempt.score, status: previousModule.quiz.bestAttempt.passed ? tObj.modals.moduleLock.passed() : tObj.modals.moduleLock.failed() })
                        : tObj.modals.moduleLock.quizNotStarted()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 w-full sm:w-auto text-sm sm:text-base"
          >
            {tObj.modals.moduleLock.close()}
          </Button>
          {previousLesson && (
            <Button
              onClick={handleGoToPreviousLesson}
              className="flex-1 w-full sm:w-auto bg-[#141835] hover:bg-[#1a1f4a] text-white text-sm sm:text-base"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{tObj.modals.moduleLock.goToPreviousLesson?.() || "Aller au chapitre précédente"}</span>
              <span className="sm:hidden">Chapitre précédent</span>
            </Button>
          )}
          {previousModule && !previousLesson && (
            <Button
              onClick={handleGoToPreviousModule}
              className="flex-1 w-full sm:w-auto bg-[#141835] hover:bg-[#1a1f4a] text-white text-sm sm:text-base"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              {tObj.modals.moduleLock.goToPrevious()}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

